/**
 * The status badge of a row is a button: one click opens "Change status" (the seven statuses people
 * use, a dot each, a tick on the current one) and "Log consultant reply…". Picking a status saves it
 * right away (PUT /shop-drawings/{id}); the shell's toast offers Undo.
 * Without `onSetStatus` (BUG-002: Shop Drawings is View only for the role, or a Viewer account) the badge is plain
 * text — no menu.
 */
import { useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Pause, Reply } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StatusPill } from '@/components/ui/status-pill';
import { getErrorMessage } from '@/lib/errors';
import { getStatusTone } from '@/lib/status';
import { cn } from '@/lib/utils';
import { QUICK_STATUSES, STATUS_LABELS, labelize } from './model';

const moveFocus = (container, step) => {
  const items = [...(container?.querySelectorAll('[role="menuitemradio"], [role="menuitem"]') || [])].filter((item) => !item.disabled);
  if (!items.length) return;
  const index = items.indexOf(document.activeElement);
  const next = index < 0 ? (step > 0 ? 0 : items.length - 1) : (index + step + items.length) % items.length;
  items[next]?.focus();
};

export function StatusMenu({ drawing, onSetStatus, onReply, replyLabel = 'Log consultant reply…', className }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const status = String(drawing.status || '').toUpperCase();
  const label = labelize(status, STATUS_LABELS);
  const options = QUICK_STATUSES.includes(status) || !status ? QUICK_STATUSES : [...QUICK_STATUSES, status];

  if (!onSetStatus) {
    return (
      <span className={cn('-ml-1 inline-flex max-w-full items-center py-0.5 pl-1', className)} data-testid={`status-badge-${drawing.id}`}>
        <StatusPill status={status} domain="shop_drawing" label={label} />
      </span>
    );
  }

  const onOpenChange = (next) => {
    if (busy) return;
    setOpen(next);
    if (next) setError('');
  };

  const pick = async (next) => {
    if (next === status) {
      setOpen(false);
      return;
    }
    setBusy(next);
    setError('');
    try {
      await onSetStatus(drawing, next);
      setOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not change the status'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'group/status -ml-1 inline-flex max-w-full items-center gap-0.5 rounded-full py-0.5 pl-1 pr-1 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-surface-hover',
            className
          )}
          aria-label={`Status: ${label || 'none'}. Change status`}
          data-testid={`status-menu-${drawing.id}`}
        >
          <StatusPill status={status} domain="shop_drawing" label={label} />
          <ChevronDown className="h-3 w-3 shrink-0 text-ink-3 opacity-0 transition-opacity group-hover/status:opacity-100 group-focus-visible/status:opacity-100 group-data-[state=open]/status:opacity-100" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[232px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const checked = listRef.current?.querySelector('[aria-checked="true"]');
          if (checked) checked.focus();
          else moveFocus(listRef.current, 1);
        }}
      >
        <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3" id={`status-menu-title-${drawing.id}`}>Change status</p>
        <div
          ref={listRef}
          role="menu"
          aria-labelledby={`status-menu-title-${drawing.id}`}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              moveFocus(listRef.current, event.key === 'ArrowDown' ? 1 : -1);
            }
          }}
        >
          {options.map((value) => {
            const current = value === status;
            const tone = getStatusTone(value, 'shop_drawing');
            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={current}
                disabled={Boolean(busy)}
                onClick={() => pick(value)}
                className={cn(
                  'flex h-8 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] text-ink outline-none hover:bg-surface-hover focus-visible:bg-surface-hover disabled:opacity-60',
                  current && 'bg-surface-2 font-semibold'
                )}
                data-testid={`set-status-${value}`}
              >
                {value === 'ON_HOLD' ? (
                  <Pause className="h-3 w-3 shrink-0 text-status-amber" strokeWidth={3} aria-hidden="true" />
                ) : (
                  <span className={cn('ui-tone-dot shrink-0', `tone-${tone}`)} style={{ width: 8, height: 8 }} aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1 truncate">{labelize(value, STATUS_LABELS)}</span>
                {busy === value ? <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-3" aria-hidden="true" /> : null}
                {current && busy !== value ? <Check className="h-3.5 w-3.5 text-brand-ink" aria-hidden="true" /> : null}
              </button>
            );
          })}
          {onReply ? (
            <>
              <div className="my-1 h-px bg-line-2" role="separator" />
              <button
                type="button"
                role="menuitem"
                disabled={Boolean(busy)}
                onClick={() => {
                  setOpen(false);
                  onReply(drawing);
                }}
                className="flex h-8 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] text-ink outline-none hover:bg-surface-hover focus-visible:bg-surface-hover"
                data-testid={`status-menu-reply-${drawing.id}`}
              >
                <Reply className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
                <span className="truncate">{replyLabel}</span>
              </button>
            </>
          ) : null}
        </div>
        {error ? <p className="px-2.5 pb-1 pt-1.5 text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
      </PopoverContent>
    </Popover>
  );
}
